// shell/components/entity-fields/view-renderers/badge-renderer.ts

import { createElement } from '../../../utils/dom';
import type { BadgeViewField } from '../../entity-view/types';

/**
 * Renders a badge/pill with color based on value.
 */
export function renderBadgeView<T>(
    field: BadgeViewField<T>,
    value: unknown,
    _model: T
): HTMLElement {
    const container = createElement('div', {
        className: 'ev-field-view ev-field-view--badge',
    });

    if (value === null || value === undefined) {
        container.classList.add('ev-field-view--empty');
        container.textContent = '—';
        return container;
    }

    const stringValue = String(value);

    // Get display label
    let displayLabel = stringValue;
    if (field.labelMap && stringValue in field.labelMap) {
        displayLabel = field.labelMap[stringValue];
    }

    // Get color
    let color: string | undefined;
    if (field.colorMap && stringValue in field.colorMap) {
        color = field.colorMap[stringValue];
    }

    const badge = createElement('span', {
        className: 'ev-badge',
        textContent: displayLabel,
    });

    if (color) {
        badge.style.backgroundColor = color;
        // Calculate contrasting text color
        badge.style.color = getContrastColor(color);
    }

    container.appendChild(badge);
    return container;
}

/**
 * Calculate a contrasting text color (black or white) based on background.
 */
function getContrastColor(hexColor: string): string {
    // Handle named colors by returning default
    if (!hexColor.startsWith('#')) {
        return '#ffffff';
    }

    // Parse hex color
    let hex = hexColor.slice(1);
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.5 ? '#000000' : '#ffffff';
}
